import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cardGrid", "listPanel", "loadingIndicator", "scrollTrigger"]
  static values = {
    url: String,
    nextPage: Number,
    hasMore: Boolean,
    loading: Boolean
  }

  connect() {
    this.setupIntersectionObserver()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  setupIntersectionObserver() {
    if (!this.hasMoreValue || !this.hasScrollTriggerTarget || !("IntersectionObserver" in window)) return

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadMore()
    }, { rootMargin: "500px 0px" })

    this.observer.observe(this.scrollTriggerTarget)
  }

  async loadMore() {
    if (this.loadingValue || !this.hasMoreValue || !this.nextPageValue) return

    this.loadingValue = true
    this.loadingIndicatorTarget.classList.remove("hidden")
    this.loadingIndicatorTarget.classList.add("flex")

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("page", this.nextPageValue)

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      if (!response.ok) throw new Error(`Entry batch request failed: ${response.status}`)

      const data = await response.json()
      this.appendHTML(this.cardGridTarget, data.cards)
      this.appendHTML(this.listPanelTarget, data.list_rows)
      this.nextPageValue = data.next_page || 0
      this.hasMoreValue = data.has_more === true

      this.element.dispatchEvent(new CustomEvent("entries:appended", { bubbles: true }))
      if (!this.hasMoreValue) this.finish()
    } catch (error) {
      console.error(error)
      this.observer?.disconnect()
      this.scrollTriggerTarget.innerHTML = '<button type="button" class="home-hero-action-link inline-flex min-h-11 items-center justify-center rounded-full border px-5 py-2.5 text-sm font-semibold" data-action="entry-loader#retry">Load more homes</button>'
    } finally {
      this.loadingValue = false
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.add("hidden")
        this.loadingIndicatorTarget.classList.remove("flex")
      }
    }
  }

  retry() {
    this.scrollTriggerTarget.innerHTML = '<div class="hidden items-center gap-3 text-sm font-semibold text-slate-600" data-entry-loader-target="loadingIndicator" role="status" aria-live="polite"><span class="event-entry-results-spinner" aria-hidden="true"></span>Loading more homes…</div>'
    this.loadMore()
  }

  appendHTML(target, html) {
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    target.append(template.content)
  }

  finish() {
    this.observer?.disconnect()
    this.scrollTriggerTarget.remove()
  }
}
